/**
 * BillingInvoices — admin invoice list with filters + bulk generate.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Filter } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";
import { StatusPill, money } from "./BillingDashboard";

const STATUSES = ["draft", "issued", "pending", "partial", "paid", "overdue", "refunded", "adjusted", "void"];

export default function BillingInvoices() {
  const { t } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showGenerate, setShowGenerate] = useState(false);
  const [gen, setGen] = useState({
    period_start: "",
    period_end: "",
    auto_issue: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const url = statusFilter
        ? `/admin/billing/invoices?status_=${statusFilter}`
        : "/admin/billing/invoices";
      const r = await api.get(url);
      setInvoices(r.data || []);
    } catch (e) {
      // ignore
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return invoices;
    const q = search.toLowerCase();
    return invoices.filter(
      (inv) =>
        (inv.invoice_number || "").toLowerCase().includes(q) ||
        (inv.parent_name || "").toLowerCase().includes(q) ||
        (inv.parent_email || "").toLowerCase().includes(q) ||
        (inv.child_name || "").toLowerCase().includes(q)
    );
  }, [invoices, search]);

  const submitGenerate = async () => {
    if (!gen.period_start || !gen.period_end) {
      toast.error("Please pick both dates.");
      return;
    }
    try {
      const r = await api.post("/admin/billing/invoices/generate", gen);
      toast.success(
        `${(r.data || []).length} invoices ${gen.auto_issue ? "issued" : "drafted"}.`
      );
      setShowGenerate(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.errors.generic"));
    }
  };

  return (
    <main className="p-6 lg:p-10 space-y-6" data-testid="admin-billing-invoices">
      <AdminHeader
        eyebrow={t("billing.invoices.eyebrow")}
        title={t("billing.invoices.title")}
        action={
          <button
            onClick={() => setShowGenerate(true)}
            data-testid="billing-generate-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
          >
            <Plus size={14} strokeWidth={2} />
            {t("billing.invoices.generate_cta")}
          </button>
        }
      />

      <section className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={14}
            strokeWidth={1.6}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
          />
          <input
            data-testid="billing-invoices-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("billing.invoices.search_placeholder")}
            className="w-full pl-9 pr-3 py-2 rounded-full border border-[#EEF0F4] bg-white text-sm focus:outline-none focus:border-[#1F3A2A]"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} strokeWidth={1.6} className="text-[#6B7280]" />
          <select
            data-testid="billing-invoices-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm bg-white border border-[#EEF0F4] rounded-full px-3 py-2 focus:outline-none focus:border-[#1F3A2A]"
          >
            <option value="">{t("billing.invoices.filter_all")}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`billing.status.${s}`)}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[#6B7280] uppercase tracking-[0.1em] text-[10px] bg-[#FAFAFB]">
              <tr>
                <th className="px-5 py-3">{t("billing.invoices.table_number")}</th>
                <th className="px-5 py-3">{t("billing.invoices.table_family")}</th>
                <th className="px-5 py-3">{t("billing.invoices.table_child")}</th>
                <th className="px-5 py-3">{t("billing.invoices.table_room")}</th>
                <th className="px-5 py-3">{t("billing.invoices.table_due")}</th>
                <th className="px-5 py-3">{t("billing.invoices.table_total")}</th>
                <th className="px-5 py-3">{t("billing.invoices.table_balance")}</th>
                <th className="px-5 py-3">{t("billing.invoices.table_status")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center font-serif italic text-[#9CA3AF]">
                    {t("billing.common.loading")}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    data-testid="billing-invoices-empty"
                    className="py-10 text-center font-serif italic text-[#9CA3AF]"
                  >
                    {t("billing.invoices.empty_state")}
                  </td>
                </tr>
              ) : (
                filtered.map((inv) => (
                  <tr
                    key={inv.id}
                    data-testid={`billing-invoice-row-${inv.id}`}
                    className="border-t border-[#EEF0F4] hover:bg-[#FAFAFB] cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/admin/billing/invoices/${inv.id}`}
                        className="font-medium text-[#1A1B2E] hover:text-[#1F3A2A]"
                      >
                        {inv.invoice_number}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-[#1A1B2E]">{inv.parent_name || "—"}</td>
                    <td className="px-5 py-3 text-[#6B7280]">{inv.child_name || "—"}</td>
                    <td className="px-5 py-3 text-[#6B7280]">{inv.room_name || "—"}</td>
                    <td className="px-5 py-3 text-[#6B7280] tabular-nums">{inv.due_at || "—"}</td>
                    <td className="px-5 py-3 tabular-nums">{money(inv.total, inv.currency)}</td>
                    <td className="px-5 py-3 tabular-nums">{money(inv.amount_due, inv.currency)}</td>
                    <td className="px-5 py-3">
                      <StatusPill status={inv.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showGenerate && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="billing-generate-modal"
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setShowGenerate(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-xl text-[#1A1B2E]">
              {t("billing.invoices.generate_cta")}
            </h3>
            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="eyebrow">{t("billing.invoices.generate_period_start")}</span>
                <input
                  type="date"
                  data-testid="billing-generate-start"
                  value={gen.period_start}
                  onChange={(e) => setGen({ ...gen, period_start: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
                />
              </label>
              <label className="block">
                <span className="eyebrow">{t("billing.invoices.generate_period_end")}</span>
                <input
                  type="date"
                  data-testid="billing-generate-end"
                  value={gen.period_end}
                  onChange={(e) => setGen({ ...gen, period_end: e.target.value })}
                  className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm focus:outline-none focus:border-[#1F3A2A]"
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-[#1A1B2E]">
                <input
                  type="checkbox"
                  data-testid="billing-generate-auto-issue"
                  checked={gen.auto_issue}
                  onChange={(e) => setGen({ ...gen, auto_issue: e.target.checked })}
                />
                {t("billing.invoices.generate_auto_issue")}
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowGenerate(false)}
                className="text-sm text-[#6B7280] hover:text-[#1A1B2E]"
                data-testid="billing-generate-cancel"
              >
                {t("billing.common.cancel")}
              </button>
              <button
                onClick={submitGenerate}
                data-testid="billing-generate-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
              >
                {t("billing.invoices.generate_submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
